import { ActionTree, MutationTree } from 'vuex'
import {
    IStateRoot,
    IStateOrder,
    IOrder,
    IShippingAddress,
    IShipping,
    IShippingContactData,
    IShippingMethod,
    IShippingRequestPayload,
    IPaymentMethod,
    IPaymentRequestPayload,
    IPayment,
} from '@/models'
import { MUTATIONS_ORDER, ACTIONS_ORDER, DATA_FIELD_ROLE } from '@/enums'
import { API_ORDER, API_SHIPPING, API_PAYMENT } from '@/api'
import { dispatchErrorToast } from '../utils'
import { setDataField } from '@/utils'
import { getOrderCodeError } from './utils'

const state = (): IStateOrder => ({
    order: undefined,
    shippingAddress: undefined,
    shippingContactData: undefined,
    shipping: undefined,
    paymentMethod: undefined,
})

const mutations: MutationTree<IStateOrder> = {
    [MUTATIONS_ORDER.SET_ORDER](state, order?: IOrder) {
        state.order = order
    },

    [MUTATIONS_ORDER.SET_SHIPPING_ADDRESS](state, address?: IShippingAddress) {
        state.shippingAddress = address
    },

    [MUTATIONS_ORDER.SET_SHIPPING_CONTACT_DATA](state, contactData?: IShippingContactData) {
        state.shippingContactData = contactData
    },

    [MUTATIONS_ORDER.SET_SHIPPING](state, shipping?: IShipping) {
        state.shipping = shipping
    },

    [MUTATIONS_ORDER.SET_PAYMENT_METHOD](state, method?: IPaymentMethod) {
        state.paymentMethod = method
    },
}

const actions: ActionTree<IStateOrder, IStateRoot> = {
    async [ACTIONS_ORDER.FETCH_ORDER_CODE](
        { dispatch },
        payload: { email: string; orderNumber: string }
    ): Promise<string | void> {
        try {
            return await API_ORDER.fetchOrderCode(payload)
        } catch (error) {
            dispatchErrorToast(dispatch, getOrderCodeError(error, payload.orderNumber))
        }
    },

    async [ACTIONS_ORDER.FETCH_AND_SET_ORDER](
        { commit, dispatch },
        orderCode: string
    ): Promise<void> {
        try {
            const order = await API_ORDER.fetchOrderDefaultShippingAddress(orderCode)

            commit(MUTATIONS_ORDER.SET_ORDER, order)
        } catch (error) {
            dispatchErrorToast(dispatch, error)

            return Promise.reject()
        }
    },

    async [ACTIONS_ORDER.FETCH_SHIPPING_METHODS](
        { state, dispatch },
        shippingAddress: IShippingAddress
    ): Promise<IShippingMethod[] | void> {
        const orderCode = state.order!.orderCode

        try {
            return await API_SHIPPING.fetchShippingMethods(orderCode, shippingAddress)
        } catch (error) {
            dispatchErrorToast(dispatch, error)

            return Promise.reject()
        }
    },

    async [ACTIONS_ORDER.CREATE_AND_SET_SHIPPING](
        { state, commit, dispatch },
        method: IShippingMethod
    ): Promise<void> {
        const orderCode = state.order!.orderCode
        const fields = method.fields

        setDataField(
            fields,
            DATA_FIELD_ROLE.CONTACT_DATA_PHONE,
            state.shippingContactData!.phoneNo
        )

        const payload: IShippingRequestPayload = {
            methodID: method.methodID,
            fields: fields.reduce((result, field) => {
                result[field.name] = field.value

                return result
            }, {} as any),
        }

        commit(MUTATIONS_ORDER.SET_SHIPPING, undefined)

        try {
            const shipping = await API_SHIPPING.createShipping(orderCode, payload)

            commit(MUTATIONS_ORDER.SET_SHIPPING, shipping)
        } catch (error) {
            dispatchErrorToast(dispatch, error)

            return Promise.reject()
        }
    },

    async [ACTIONS_ORDER.FETCH_PAYMENT_METHODS](
        { dispatch },
        shipping: IShipping
    ): Promise<IPaymentMethod[] | void> {
        try {
            return await API_PAYMENT.fetchPaymentMethods(shipping.shippingID)
        } catch (error) {
            dispatchErrorToast(dispatch, error)

            return Promise.reject()
        }
    },

    async [ACTIONS_ORDER.CREATE_PAYMENT](
        { state, dispatch },
        {
            orderCode,
            shippingId,
            paymentMethod,
        }: { orderCode: string; shippingId: string; paymentMethod: IPaymentMethod }
    ): Promise<void> {
        const fields = paymentMethod.fields
        const phoneNo = state.shippingContactData?.phoneNo
        const backUrl = `${location.origin}/orders/${orderCode}/payments/$PAYMENTID$`

        if (phoneNo) {
            setDataField(fields, DATA_FIELD_ROLE.CONTACT_DATA_PHONE, phoneNo, { silent: true })
        }
        setDataField(fields, DATA_FIELD_ROLE.PAYMENT_METHOD_RETURN_URL, backUrl, {
            silent: true,
        })
        setDataField(fields, DATA_FIELD_ROLE.PAYMENT_METHOD_CANCEL_URL, backUrl, {
            silent: true,
        })
        setDataField(fields, DATA_FIELD_ROLE.PAYMENT_METHOD_CONTINUE_URL, backUrl, {
            silent: true,
        })

        const payload: IPaymentRequestPayload = {
            methodID: paymentMethod.methodID,
            fields: fields.reduce((result, field) => {
                result[field.name] = field.value

                return result
            }, {} as any),
        }

        try {
            const { redirectToURL } = await API_PAYMENT.createPayment(shippingId, payload)

            redirectToURL ? (window.location.href = redirectToURL) : location.reload()
        } catch (error) {
            dispatchErrorToast(dispatch, error)

            return Promise.reject()
        }
    },

    async [ACTIONS_ORDER.FETCH_PAYMENT_ID](
        { dispatch },
        orderCode: string
    ): Promise<string | void> {
        try {
            return await API_PAYMENT.fetchPaymentId(orderCode)
        } catch (error) {
            dispatchErrorToast(dispatch, error)
        }
    },

    async [ACTIONS_ORDER.FETCH_PAYMENT](
        { dispatch },
        paymentId: string
    ): Promise<IPayment | void> {
        try {
            return await API_PAYMENT.fetchPayment(paymentId)
        } catch (error) {
            dispatchErrorToast(dispatch, error)
        }
    },

    async [ACTIONS_ORDER.FETCH_SHIPPING_ID](
        { dispatch },
        orderCode: string
    ): Promise<string | void> {
        try {
            return await API_SHIPPING.fetchShippingId(orderCode)
        } catch (error) {
            dispatchErrorToast(dispatch, error)
        }
    },

    async [ACTIONS_ORDER.FETCH_SHIPPING](
        { dispatch },
        shippingId: string
    ): Promise<IShipping | void> {
        try {
            return await API_SHIPPING.fetchShipping(shippingId)
        } catch (error) {
            dispatchErrorToast(dispatch, error)
        }
    },
}

export const storeModuleOrder = {
    namespaced: true,
    state,
    mutations,
    actions,
}
